import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// components and helpers
import { workspaceActions } from '../WorkspaceActions';
import * as dashboardActions from '../Dashboard/DashboardActions';
import ImportDummyDialog from './ImportWizard/ImportDummyDialog';
import DownloadTemplatesDialog from './DownloadTemplatesDialog';
import UploadFileDialog from './ImportWizard/UploadFileDialog';
import FileList from './FileList';
import templateImage from '../../../assets/img/TemplateGraphic.svg';
import InfoCarousel from '../../Shared/InfoCarousel';
import tipsIcon from '../../../assets/img/fa6-solid_lightbulb.svg';
import tipsIconDark from '../../../assets/img/lightbulb_dark.svg';

// mui
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';

const DataSources: React.FC = () => {
    const theme = useTheme();
    const location = useLocation();
    const dispatch = useDispatch();
    const [openDemo, setOpenDemo] = React.useState(false);
    const [openWizard, setOpenWizard] = React.useState(false);
    const [openTemplate, setOpenTemplate] = React.useState(false);
    const dashboard = useSelector((state: any) => state.dashboard);
    const importTips = [
        {
            title: 'Clean Up Your File',
            icon: theme.palette.mode === 'light' ? tipsIcon : tipsIconDark,
            description:
                'You’ll have fewer errors to resolve if your data file is clean and free of duplicates.',
        },
        {
            title: 'Match Your Field Names',
            icon: theme.palette.mode === 'light' ? tipsIcon : tipsIconDark,
            description:
                'You’ll be required to map your data fields to HDE data fields. Data in unmapped fields are not imported.',
        },
        {
            title: 'Create A Sandbox',
            icon: theme.palette.mode === 'light' ? tipsIcon : tipsIconDark,
            description:
                'Sandbox demo data can only be imported one at a time. Create a new workspace to create another sandbox space. ',
        },
    ];

    useEffect(() => {
        const workspaceId = location.pathname.split('/').pop();
        const query = {
            reportsCount: {
                addCount: true,
                limit: 0,
                text: '',
                initialFilter: {},
                where: {},
            },
        };
        dispatch(
            dashboardActions?.getDashboardDataMultiple(
                'CASE',
                workspaceId || '-all-',
                query
            )
        );
        dispatch(
            dashboardActions?.getDashboardDataMultiple(
                'FILE',
                workspaceId || '-all-',
                query
            )
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCreateSandboxData = () => {
        const workspaceId = location.pathname.split('/').pop();
        dispatch(workspaceActions?.createDummyCases(workspaceId)).then(() => {
            dispatch(
                workspaceActions?.getFileList(
                    workspaceId,
                    10,
                    0,
                    'lastUpdated',
                    'DESC',
                    'initial'
                )
            );
        });
        setOpenDemo(false);
    };

    const handleDownloadTemplates = (templates) => {
        const results = Object.keys(templates).filter((key) => templates[key]);
        // eslint-disable-next-line array-callback-return
        results.map((template) => {
            dispatch(workspaceActions?.downloadTemplates(template));
        });
    };

    return (
        <Grid container justifyContent={'center'} spacing={4}>
            <Grid item xs={5} sx={{ minHeight: 270 }}>
                <Paper
                    sx={{
                        padding: 2,
                        height: '100%',
                        background:
                            'linear-gradient(149deg, rgba(50,85,153,1) 35%, rgba(102,50,153,1) 100%)',
                    }}
                    elevation={0}
                >
                    <Grid
                        container
                        direction={'column'}
                        justifyContent={'space-between'}
                        spacing={6}
                    >
                        <Grid item xs={12}>
                            <Typography
                                variant={'h6'}
                                color={'#fff'}
                                sx={{ fontWeight: 'bold' }}
                            >
                                Welcome to the Data Import Wizard!
                            </Typography>
                            <Typography variant={'body2'} color={'#fff'}>
                                Import case details seamlessly for easy viewing
                                and analysis, with options for manual or
                                automated machine-to-machine imports.
                            </Typography>
                        </Grid>
                        <Grid item xs={12}></Grid>
                        <Grid item xs={12}>
                            <Grid container justifyContent={'space-between'}>
                                <Grid item>
                                    <Tooltip
                                        title={
                                            dashboard &&
                                            dashboard.caseList &&
                                            dashboard.caseList.reportsCount &&
                                            dashboard.caseList.reportsCount &&
                                            dashboard.caseList.reportsCount
                                                .items.length &&
                                            dashboard.caseList.reportsCount
                                                .items[0].COUNT > 0
                                                ? 'Workspace contains data'
                                                : null
                                        }
                                    >
                                        <span>
                                            <Button
                                                variant={'outlined'}
                                                size={'small'}
                                                sx={{
                                                    '&.MuiButton-outlined': {
                                                        border:
                                                            dashboard &&
                                                            dashboard.caseList &&
                                                            dashboard.caseList
                                                                .reportsCount &&
                                                            dashboard.caseList
                                                                .reportsCount &&
                                                            dashboard.caseList
                                                                .reportsCount
                                                                .items.length &&
                                                            dashboard.caseList
                                                                .reportsCount
                                                                .items[0]
                                                                .COUNT > 0
                                                                ? '1px solid gray'
                                                                : '1px solid white',
                                                    },
                                                    color:
                                                        dashboard &&
                                                        dashboard.caseList &&
                                                        dashboard.caseList
                                                            .reportsCount &&
                                                        dashboard.caseList
                                                            .reportsCount &&
                                                        dashboard.caseList
                                                            .reportsCount.items
                                                            .length &&
                                                        dashboard.caseList
                                                            .reportsCount
                                                            .items[0].COUNT > 0
                                                            ? '#8D94BE !important'
                                                            : '#fff !important',
                                                }}
                                                onClick={() =>
                                                    setOpenDemo(true)
                                                }
                                                disabled={
                                                    (dashboard &&
                                                        dashboard.caseList &&
                                                        dashboard.caseList
                                                            .reportsCount &&
                                                        dashboard.caseList
                                                            .reportsCount &&
                                                        dashboard.caseList
                                                            .reportsCount.items
                                                            .length &&
                                                        dashboard.caseList
                                                            .reportsCount
                                                            .items[0].COUNT >
                                                            0) ||
                                                    false
                                                }
                                            >
                                                Import Sandbox Demo Data
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </Grid>
                                <Grid item>
                                    <Button
                                        size={'small'}
                                        variant={'contained'}
                                        sx={{
                                            bgcolor: '#fff',
                                            color: '#663299',
                                            fontWeight: 'bold',
                                            '&:hover': {
                                                bgcolor: '#e6e1f9',
                                            },
                                        }}
                                        onClick={() => setOpenWizard(true)}
                                    >
                                        Launch Data Import Wizard!
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
            <Grid item xs={4}>
                <InfoCarousel data={importTips} />
            </Grid>
            <Grid item xs={3}>
                <Paper sx={{ padding: 2, height: '100%' }} elevation={0}>
                    <Grid container justifyContent={'center'} spacing={2}>
                        <Grid item>
                            <img src={templateImage} alt="" width={170} />
                        </Grid>
                        <Grid item>
                            <Typography variant={'body2'} color={'primary'}>
                                Download the HDE case import template to assist
                                in easing the mapping process.
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Button
                                size={'small'}
                                variant={'outlined'}
                                onClick={() => setOpenTemplate(true)}
                            >
                                Download Templates
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <FileList />
            </Grid>
            <ImportDummyDialog
                open={openDemo}
                handleClose={() => setOpenDemo(false)}
                handleImport={handleCreateSandboxData}
            />
            <UploadFileDialog
                open={openWizard}
                handleClose={() => setOpenWizard(false)}
            />
            <DownloadTemplatesDialog
                open={openTemplate}
                handleClose={() => setOpenTemplate(false)}
                handleDownload={handleDownloadTemplates}
            />
        </Grid>
    );
};

export default DataSources;
